import React from "react";
import {Button} from "reactstrap";
import {TaxRecord} from "models/Tax";
import {useOfficeTranslation} from "i18n/OfficeTranslation";
import {classificationKeys, classificationLabel} from "./TaxEditor";
import TaxSettlementSummary from "./TaxSettlementSummary";

interface Props {
    records: TaxRecord[];
    grouped?: boolean;
    busy: boolean;
    onEdit: (record: TaxRecord) => void;
    onRemove: (record: TaxRecord) => void;
}
export const groupTaxAllocations = (records: TaxRecord[]) => {
    const groups = new Map<string, TaxRecord[]>();
    for (const record of records) {
        const category = record.allocation!.category;
        const rows = groups.get(category) || [];
        rows.push(record);
        groups.set(category, rows);
    }
    const order = (category: string) => classificationKeys.indexOf(category as typeof classificationKeys[number]);
    return Array.from(groups, ([category, rows]) => ({category, records:rows})).sort((a,b) => order(a.category)-order(b.category));
};

const TaxRecordRow: React.FC<Omit<Props, "records" | "grouped"> & {record: TaxRecord}> = ({record:r, busy, onEdit, onRemove}) => {
    const {t, intlLocale} = useOfficeTranslation();
    const format = (amount: number | undefined) => (amount || 0).toLocaleString(intlLocale, {style:"currency", currency:"EUR"});
    return <div className="tax-record">
        <div className="tax-record-content"><strong>{r.name}</strong><small>{r.loan ? t(r.loan.direction==="issued"?"taxModule.issued":"taxModule.received") : r.participation ? `${r.participation.share}%` : r.bookyear ? `${r.bookyear.start} / ${r.bookyear.end}` : r.ledger ? `${r.ledger.account} · ${r.ledger.deductible}%` : r.allocation ? t(r.allocation.source==="expense"?"taxModule.expenseSource":"taxModule.paymentSource") : r.notes ? `${r.notes.start} / ${r.notes.end}` : ""}</small>
            {r.settlement && <><small>{r.settlement.date}</small><TaxSettlementSummary settlement={r.settlement}/></>}
            {(r.investment||r.loan||r.participation) && <span>{t("taxModule.value")}: {format(r.value)}</span>}
            {r.investment && <small>{t("taxModule.depreciation")}: {format(r.depreciation)}</small>}
            {r.equity_date && <small>{t("taxModule.equityDate")}: {r.equity_date}</small>}
        </div>
        <div className="tax-actions"><Button color="light" disabled={busy} onClick={() => onEdit(r)}>{t("taxModule.edit")}</Button><Button color="light" disabled={busy} onClick={() => onRemove(r)}>{t("taxModule.delete")}</Button></div>
    </div>;
};

const TaxRecordList: React.FC<Props> = ({records, grouped, ...actions}) => {
    const {t} = useOfficeTranslation();
    if (!records.length) return <p className="text-muted">{t("taxModule.empty")}</p>;
    const rows = (items: TaxRecord[]) => <div className="tax-records">{items.map(record => <TaxRecordRow key={record.id} record={record} {...actions}/>)}</div>;
    if (!grouped) return rows(records);
    return <div className="tax-allocation-groups">{groupTaxAllocations(records).map(group => <details className="tax-allocation-group" key={group.category} open={group.category==="mollie_settlement"}>
        <summary><h4>{t(classificationLabel(group.category))}</h4><span className="badge bg-light text-dark" aria-label={t("taxModule.groupCount", {count:group.records.length})}>{group.records.length}</span></summary>
        {rows(group.records)}
    </details>)}</div>;
};
export default TaxRecordList;
